import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Trophy, Youtube } from 'lucide-react';
import { API, getLogoUrl } from '../config/api';
import { useLanguage } from '../contexts/LanguageContext';
import ShareButtons from '../components/ShareButtons';

const ResultsPage = () => {
  const [games, setGames] = useState([]);
  const { t, language } = useLanguage();

  useEffect(() => {
    axios.get(`${API}/games?status=completed&season=1`).then(res => setGames(res.data)).catch(console.error);
  }, []);

  const groupedByWeek = games.reduce((acc, game) => {
    const week = `${t('week')} ${game.week}`;
    if (!acc[week]) acc[week] = [];
    acc[week].push(game);
    return acc;
  }, {});

  return (
    <div className="max-w-7xl mx-auto px-4 py-12" data-testid="results-page">
      <div className="text-center mb-12">
        <h1 className="section-title mb-2">{t('resultsTitle')}</h1>
        <p className="text-slate-600">{language === 'gr' ? 'Ολοκληρωμένοι αγώνες και σκορ 1ης Σεζόν' : 'Completed games and scores of Season 1'}</p>
      </div>

      {games.length === 0 ? (
        <p className="text-slate-500 text-center py-12">{t('noCompletedGames')}</p>
      ) : (
        Object.entries(groupedByWeek).map(([week, weekGames]) => (
          <div key={week} className="mb-10" data-testid={`results-week-${week}`}>
            <h2 className="text-xl font-display font-bold text-[#1E3A5F] mb-4 flex items-center">
              <Trophy className="mr-2" size={24} /> {week}
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {[...weekGames].sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time)).map(game => (
                <div key={game.id} className="card p-6" data-testid={`result-${game.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-slate-500">{game.date}</span>
                    <div className="flex items-center gap-2">
                      {game.forfeit_team && <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">{language === 'gr' ? 'Αποχώρηση' : 'Forfeit'}</span>}
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">{t('final')}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex-1 flex items-center gap-2 justify-end">
                      <span className={`font-bold text-lg ${game.home_score > game.away_score ? 'text-[#1E3A5F]' : 'text-slate-500'}`}>
                        {game.home_team_name}
                      </span>
                      {game.home_team_id && <img src={getLogoUrl(game.home_team_id)} alt="" className="w-9 h-9 rounded-full object-cover" onError={e => { e.target.style.display='none'; }} />}
                    </div>
                    <div className="score-display text-[#1E3A5F] mx-4">
                      {game.home_score} - {game.away_score}
                    </div>
                    <div className="flex-1 flex items-center gap-2">
                      {game.away_team_id && <img src={getLogoUrl(game.away_team_id)} alt="" className="w-9 h-9 rounded-full object-cover" onError={e => { e.target.style.display='none'; }} />}
                      <span className={`font-bold text-lg ${game.away_score > game.home_score ? 'text-[#1E3A5F]' : 'text-slate-500'}`}>
                        {game.away_team_name}
                      </span>
                    </div>
                  </div>
                  
                  {/* YouTube Video Link - Always show with red icon */}
                  {game.video_url ? (
                    <a 
                      href={game.video_url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="flex flex-col items-center gap-2 p-4 bg-red-50 rounded-lg hover:bg-red-100 transition group"
                      data-testid={`video-link-${game.id}`}
                    >
                      <span className="text-sm text-slate-700 group-hover:text-red-700 font-medium">{t('watchVideo')}</span>
                      <Youtube size={36} className="text-red-600" />
                    </a>
                  ) : (
                    <div className="flex flex-col items-center gap-2 p-4 bg-slate-50 rounded-lg">
                      <span className="text-sm text-slate-500">{t('watchVideo')}</span>
                      <Youtube size={36} className="text-red-400" />
                      <span className="text-xs text-slate-400">(coming soon)</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))
      )}
      <div className="mt-4 flex justify-end">
        <ShareButtons title={language === 'gr' ? 'Αποτελέσματα WBLA' : 'WBLA Results'} />
      </div>
    </div>
  );
};

export default ResultsPage;
